import React from "react";
import { Clock, MapPin, Home, Wifi, AlertCircle } from "lucide-react";

const GuestInformation = () => {
  return (
    <div className="space-y-8">
      {/* Intro */}
      <section>
        <h2 className="text-2xl font-bold mb-2">Staying at our place</h2>
        <p className="text-gray-700">
          A few notes to keep things running smoothly — and to make life easy with the neighbours and the building.
        </p>
      </section>

      {/* Home essentials */}
      <section>
        <div className="flex items-center mb-3">
          <Home className="mr-2 text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">A few important things about our home</h3>
        </div>

        <ul className="ml-8 space-y-2 text-gray-700">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Make yourself at home — feel free to use the kitchen, basic supplies, and toiletries. You’re also welcome to
              use the <span className="font-medium">Apple TV</span> and speakers (Bluetooth).
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Please don’t open any <span className="font-medium">sealed</span> units (white bedroom storage, sealed
              kitchen cupboard) — those contain personal things.
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Shower/bath: please use the <span className="font-medium">squeegee</span> on the window ledge and crack the
              window briefly after showering (there’s no fan).
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Please <span className="font-medium">don’t smoke</span> inside the apartment.
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Neighbours: small gatherings only — no parties (max <span className="font-medium">8 people</span>). Quiet
              hours are <span className="font-medium">10pm–6am</span>.
            </span>
          </li>

          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Elevator: it goes to floors <span className="font-medium">2</span> or <span className="font-medium">3</span>
              , then it’s half a flight of stairs to the apartment.
            </span>
          </li>
        </ul>
      </section>

      {/* Location & transport */}
      <section>
        <div className="flex items-center mb-3">
          <MapPin className="mr-2 text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">Location & transport</h3>
        </div>

        <div className="ml-8 space-y-2 text-gray-700">
          <p>
            <span className="font-medium">Nearest U-Bahn:</span> Rosenthaler Platz (U8)
          </p>
          <p>
            <span className="font-medium">Other transport:</span> Multiple S-Bahn lines, buses, and trams nearby
          </p>
          <p className="text-sm">
            <span className="font-medium">From BER airport:</span> Take the{" "}
            <span className="font-medium">Flughafen Express (FEX)</span> to{" "}
            <span className="font-medium">Hauptbahnhof</span>, then the <span className="font-medium">M8 tram</span> or a
            taxi.
          </p>
          <p className="text-sm text-gray-600">
            Parking is limited and usually not free. If you need a car park,{" "}
            <span className="font-medium">Alexa Shopping Center</span> is a straightforward option (Grunerstraße 20, 10179
            Berlin).
          </p>
        </div>
      </section>

      {/* Practical info */}
      <section>
        <div className="flex items-center mb-3">
          <Clock className="mr-2 text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">Practical info</h3>
        </div>

        <div className="ml-8 space-y-3 text-gray-700">
          <div>
            <p>
              <span className="font-medium">Address:</span> Torstraße 138, 10119 Berlin
            </p>
          </div>

          <div className="space-y-1">
            <p>
              <span className="font-medium">Arrival:</span> From <span className="font-medium">3:00 PM</span>
            </p>
            <p>
              <span className="font-medium">Departure:</span> By <span className="font-medium">11:00 AM</span>
            </p>
            <p className="text-sm text-gray-600">If there’s no same-day overlap, we can often be flexible — just ask.</p>
            <p className="text-sm">
              When you leave, please put <span className="font-medium">both keys</span> inside the apartment by the door.
              If you have a minute, run the dishwasher (or wash any dishes you used).
            </p>
          </div>

          <div className="space-y-1">
            <p>
              <span className="font-medium">Washing machine:</span> Available
            </p>
            <p>
              <span className="font-medium">Nespresso machine:</span> Available
            </p>
          </div>

          <div className="space-y-1">
            <p>
              <span className="font-medium">Rubbish:</span> Bins are in the courtyard (general, paper, packaging, glass)
            </p>
            <p className="text-sm text-gray-600">
              Heating is automatic — please don’t leave windows open for long periods.
            </p>
          </div>
        </div>
      </section>

      {/* Wi-Fi */}
      <section>
        <div className="flex items-center mb-3">
          <Wifi className="mr-2 text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">Wi-Fi</h3>
        </div>

        <div className="ml-8 space-y-1 text-gray-700">
          <p>
            <span className="font-medium">Network:</span> Shared at check-in
          </p>
          <p>
            <span className="font-medium">Password:</span> Shared at check-in
          </p>
          <p className="text-sm text-gray-600">If it drops, toggling Wi-Fi off/on on your device usually fixes it.</p>
        </div>
      </section>

      {/* Emergencies */}
      <section>
        <div className="flex items-center mb-3">
          <AlertCircle className="mr-2 text-blue-600" size={24} />
          <h3 className="text-xl font-semibold">Emergency numbers</h3>
        </div>

        <div className="ml-8 text-gray-700">
          <p>
            <span className="font-medium">112</span> (fire/medical) •{" "}
            <span className="font-medium">110</span> (police)
          </p>
        </div>
      </section>
    </div>
  );
};

export default GuestInformation;